// TruthU — X Coach (standalone)
// Entry point. Boots config from /api/lab/config, paints the landing, and
// dispatches into the 5-step tour scenes on demand. Heavy modules: api,
// state, icons. Everything else lives here for now — this file is the
// orchestrator, not the kitchen sink.

import './styles.css';
import { api }   from './api.js';
import { state } from './state.js';
import { ICON }  from './icons.js';
import { t, applyI18n, setLocale, LANG_LABELS, SUPPORTED_LOCALES } from './i18n.js';

// Bootstrap constants — populated in the async boot below.
let MODELS         = [];
let DEFAULT_MODEL  = '';
let HOOK_STYLES    = [];
let TWEET_MAX      = 280;
let CREDITS_PER_USD = 100;
const IS_MOBILE = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

const $  = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const toast = (msg, ms = 1800) => {
  const t = $('#toast');
  t.textContent = msg;
  t.classList.remove('hidden');
  clearTimeout(toast._t);
  toast._t = setTimeout(() => t.classList.add('hidden'), ms);
};


  // Fire-and-forget prefetch: warms the X cache while the user finishes
  // the tour, so the generate phase skips ~10-15s of X-API time.
  const prefetchSource = () => {
    if (!state.sourceType || !state.sourceId) return;
    api.prefetch({ source_type: state.sourceType, source_id: state.sourceId });
  };

  // ---------- view switcher ----------
  // Landing has multiple variants (default + 3 positioning pages). They all
  // share the same DOM container set; the router below decides which is
  // visible. The tour / paywall / generating / results / history views are
  // independent of routing.
  const LANDING_VIEWS = ['landing', 'landing-suspension', 'landing-viral', 'landing-variations'];
  const VIEWS = [...LANDING_VIEWS, 'tour', 'paywall', 'generating', 'results', 'history'];
  const isLanding = (name) => LANDING_VIEWS.includes(name);
  const showView = (name) => {
    VIEWS.forEach(v => {
      const el = $('#view-' + v);
      if (el) el.classList.toggle('hidden', v !== name);
    });
    $('#topmenu').classList.toggle('hidden', !state.me);
    $('#topbar-progress').classList.toggle('hidden', name !== 'tour');
    if (!isLanding(name)) window.scrollTo({top: 0, behavior: 'instant'});
  };

  // ---------- router ----------
  // Path-based URLs are the canonical, SEO-indexable form
  // (/avoid-suspension/, /viral-search/, /many-variations/). Each is a
  // separate prerendered HTML in dist/<slug>/index.html — served by nginx
  // directly, with its own <title>/og:* baked in.
  //
  // Hash routes (#/avoid-suspension) stay as client-side fallback so old
  // tweets and bookmarks don't 404 — they paint the same view but on the
  // default landing's URL.
  const ROUTES = {
    '':                   'landing',
    '/':                  'landing',
    '/avoid-suspension':  'landing-suspension',
    '/viral-search':      'landing-viral',
    '/many-variations':   'landing-variations',
  };
  const VARIANT_BY_VIEW = {
    'landing':              'default',
    'landing-suspension':   'suspension',
    'landing-viral':        'viral',
    'landing-variations':   'variations',
  };
  // Per-route document.title — kept in sync with the prerendered HTMLs so
  // that hash-based fallback paints (e.g. user on / opens #/viral-search
  // client-side) update the tab title.
  const TITLE_BY_VIEW = {
    'landing':              'TruthU · Your daily X reply pack, in your voice, 7 languages',
    'landing-suspension':   'Stay unflagged on X · TruthU drafts, you post manually',
    'landing-viral':        'Find viral tweets to reply to · TruthU mines your X Lists',
    'landing-variations':   'Every tweet, 23 angles · 7 hook styles · TruthU X Coach',
  };
  const resolveView = () => {
    // 1. Real path first (the prerendered HTMLs land on these URLs).
    //    Strip trailing slash so '/viral-search/' and '/viral-search' map
    //    to the same key in ROUTES.
    const path = location.pathname.replace(/\/$/, '');
    if (ROUTES[path]) return ROUTES[path];
    if (path === '') return 'landing';
    // 2. Hash fallback (backward compat with old #/foo links).
    const hash = location.hash.replace(/^#/, '');
    if (ROUTES[hash]) return ROUTES[hash];
    // 3. Anything else (e.g. /lab, /admin, /whatever) → default landing.
    return 'landing';
  };
  const handleRoute = () => {
    const view = resolveView();
    state.landingVariant = VARIANT_BY_VIEW[view] || 'default';
    try { localStorage.setItem('hybrid.entryVariant', state.landingVariant); } catch {}
    const wantTitle = TITLE_BY_VIEW[view];
    if (wantTitle && document.title !== wantTitle) document.title = wantTitle;
    showView(view);
  };
  window.addEventListener('hashchange', handleRoute);
  window.addEventListener('popstate',   handleRoute);

  // ---------- stickiness helpers (low-credit toast, 24-h follow-up, re-run) ----------
  // Defined here so the boot block can call them safely. The functions are
  // safe no-ops when the corresponding DOM nodes don't exist yet — that's
  // the case briefly while the views are still rendering.
  const persistLastJob = (jobId) => {
    state.lastJobAt = Date.now();
    state.lastJobId = jobId || state.mainJobId || null;
    try {
      localStorage.setItem('hybrid.lastJobAt', String(state.lastJobAt));
      if (state.lastJobId) localStorage.setItem('hybrid.lastJobId', state.lastJobId);
    } catch {}
    paintFollowupBanner();
  };
  const persistLastSource = (name) => {
    if (!state.sourceType || !state.sourceId) return;
    state.lastSource = { type: state.sourceType, id: state.sourceId, name: name || '' };
    try { localStorage.setItem('hybrid.lastSource', JSON.stringify(state.lastSource)); } catch {}
  };
  const paintFollowupBanner = () => {
    const banner = $('#followup-banner');
    if (!banner) return;
    if (!state.me || !state.lastJobAt) { banner.classList.add('hidden'); return; }
    const age = Date.now() - state.lastJobAt;
    const SHOW_FROM = 18 * 3600 * 1000;
    const SHOW_TO   = 36 * 3600 * 1000;
    if (age < SHOW_FROM || age > SHOW_TO) { banner.classList.add('hidden'); return; }
    banner.classList.remove('hidden');
  };
  const paintLowCreditToast = () => {
    if (!state.me) return;
    if (state.me.credits !== 1) return;
    // Single, non-blocking toast with a deep-link to the paywall at $10.
    toast('1 credit left — top up $10 (+2 free) before your next list goes cold');
  };
  const paintReRunCard = () => {
    const card = $('#rerun-card');
    if (!card) return;
    if (!state.me || !state.lastSource?.id) { card.classList.add('hidden'); return; }
    card.classList.remove('hidden');
    const nameEl = $('#rerun-list-name');
    if (nameEl) nameEl.textContent = state.lastSource.name || ('source ' + state.lastSource.id);
  };

  // ---------- top menu ----------
  const paintTopMenu = () => {
    $('#credit-num').textContent = (state.me?.credits ?? 0);
    const pic = state.me?.x_profile_image_url;
    const handle = state.persona?.oauth_x_username;
    const displayName = state.me?.x_display_name || state.me?.name || '';
    const img = $('#x-avatar');
    const fallback = $('#x-avatar-fallback');
    if (pic) {
      img.src = pic;
      img.classList.remove('hidden');
      fallback.classList.add('hidden');
    } else {
      img.classList.add('hidden');
      fallback.classList.remove('hidden');
      fallback.textContent = (displayName || handle || 'you').slice(0, 1).toUpperCase();
    }
    $('#x-handle').textContent = handle ? '@' + handle : (displayName || '—');
    $('#x-handle').classList.toggle('hidden', !(handle || displayName));
    $('#persona-btn').classList.toggle('hidden', !state.persona);
  };

  // Account dropdown — click to open, click outside to close.
  const toggleAccountMenu = (open) => {
    const m = $('#account-menu');
    if (!m) return;
    if (typeof open !== 'boolean') open = m.classList.contains('hidden');
    m.classList.toggle('hidden', !open);
  };
  document.addEventListener('click', (e) => {
    const trigger = $('#account-trigger');
    const menu = $('#account-menu');
    if (!trigger || !menu) return;
    if (trigger.contains(e.target)) {
      toggleAccountMenu();
    } else if (!menu.contains(e.target)) {
      toggleAccountMenu(false);
    }
  });

  // ---------- landing: example reel (X-feed-styled with engagement stats) ----------
  // Each entry mocks a generated reply that landed well in production.
  // Stats are presented as "this is what THIS reply earned on X" to set
  // the user's expectations for what their own pack can produce.
  const EXAMPLES = [
    {
      name: 'Karim Zitouni', handle: 'kztouni1', verified: true, time: '21h',
      hook: 'shortbold',
      text: "hot take:\n\nyou're not a founder if you can't meme",
      replies: 75, retweets: 12, likes: 98, views: 4900,
    },
    {
      name: 'Hubert Thieblot', handle: 'hthieblot', verified: true, time: '20h',
      hook: 'contrarian',
      text: 'The next billion-dollar founder has 9 followers on X rn.\n\nI will find you & fund you!',
      replies: 691, retweets: 55, likes: 1300, views: 50000,
    },
    {
      name: 'Dmitrii Kovanikov', handle: 'ChShersh', verified: true, time: '19h',
      hook: 'funny',
      text: 'My first programming book was "Learn Python in 24 hours".\n\n16 years later and I still don\'t know Python.',
      replies: 106, retweets: 42, likes: 1300, views: 29000,
    },
    {
      name: 'Dr Milan Milanović', handle: 'milan_milanovic', verified: true, time: '3h',
      hook: 'mock',
      text: "Software engineers, let's connect!",
      replies: 56, retweets: 0, likes: 100, views: 5300,
    },
    {
      name: 'A.I.Warper', handle: 'AIWarper', verified: true, time: '7h',
      hook: 'educational',
      text: "This is one of the better versions of this prompt template I've seen",
      replies: 485, retweets: 1100, likes: 31000, views: 7200000,
      media_mock: true,
    },
  ];

  const exampleStatRow = (e) => `
    <div class="mt-3 flex items-center justify-between max-w-[460px] text-white/55 text-[13px]">
      <div class="flex items-center gap-2 hover:text-sky-400 transition-colors">
        <span class="p-1.5 -m-1.5 rounded-full">${ICON.reply}</span>
        <span class="tabular-nums">${fmtBigNum(e.replies)}</span>
      </div>
      <div class="flex items-center gap-2 hover:text-emerald-400 transition-colors">
        <span class="p-1.5 -m-1.5 rounded-full">${ICON.retweet}</span>
        <span class="tabular-nums">${fmtBigNum(e.retweets)}</span>
      </div>
      <div class="flex items-center gap-2 hover:text-pink-400 transition-colors">
        <span class="p-1.5 -m-1.5 rounded-full">${ICON.heart}</span>
        <span class="tabular-nums">${fmtBigNum(e.likes)}</span>
      </div>
      <div class="flex items-center gap-2 hover:text-sky-400 transition-colors">
        <span class="p-1.5 -m-1.5 rounded-full">${ICON.views}</span>
        <span class="tabular-nums">${fmtBigNum(e.views)}</span>
      </div>
      <div class="flex items-center gap-1 -mr-1.5">
        <span class="p-1.5 -m-1.5 rounded-full hover:text-sky-400 transition-colors">${ICON.bookmark}</span>
        <span class="p-1.5 -m-1.5 rounded-full hover:text-sky-400 transition-colors">${ICON.share}</span>
      </div>
    </div>`;

  const renderReel = () => {
    const wraps = ['#example-reel', '#viral-reel'].map(s => $(s)).filter(Boolean);
    if (!wraps.length) return;
    const html = EXAMPLES.map((e, i) => {
      const mediaBlock = e.media_mock ? `
        <div class="mt-3 rounded-2xl overflow-hidden border border-white/10 relative aspect-video"
             style="background: linear-gradient(135deg, hsl(120 55% 32%), hsl(200 65% 28%));">
          <div class="absolute inset-0 grid place-items-center text-5xl opacity-80">⚽</div>
          <div class="absolute top-3 left-3 px-2 py-1 rounded-md bg-black/55 text-white text-[10px] font-mono">MATCHWEEK 24 · 34:08 · NAS 2 SHB 1</div>
          <div class="absolute top-3 right-3 px-2 py-1 rounded-md bg-black/55 text-white text-[10px] font-mono">SSC 1</div>
          <div class="absolute bottom-3 left-3 px-2 py-1 rounded-md bg-black/55 text-white text-[11px] font-medium flex items-center gap-1.5">
            <span>▶ 0:05</span><span class="opacity-70">From</span><span>🎬 Ciri</span>
          </div>
        </div>` : '';
      return `
        <article class="px-4 py-4 ${i ? 'border-t border-white/10' : ''} hover:bg-white/[0.02] transition-colors"
                 style="animation: drop 700ms cubic-bezier(0.16,1,0.3,1) both; animation-delay: ${i*90}ms;">
          <div class="flex gap-3">
            ${avatarHtml(e.handle, null, 11)}
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1 text-[15px] leading-tight">
                <span class="font-bold text-white truncate">${esc(e.name)}</span>
                ${e.verified ? `<span class="text-sky-400 shrink-0">${ICON.verified}</span>` : ''}
                <span class="text-white/50 truncate">@${esc(e.handle)}</span>
                <span class="text-white/50">·</span>
                <span class="text-white/50">${esc(e.time)}</span>
                <span class="ml-auto flex items-center gap-2 text-white/40">
                  <span>${ICON.mute}</span>
                  <span>${ICON.more}</span>
                </span>
              </div>
              <div class="mt-1 text-[15px] whitespace-pre-wrap text-white/95 leading-snug">${esc(e.text)}</div>
              ${mediaBlock}
              ${exampleStatRow(e)}
            </div>
          </div>
        </article>`;
    }).join('');
    wraps.forEach(w => { w.innerHTML = html; });
  };

  // ---------- pips + progress ----------
  const TOUR_LABELS = ['welcome','connect X','pick a list','replies or posts','hook styles'];
  const renderPips = () => {
    const row = $('#pip-row');
    row.innerHTML = TOUR_LABELS.map((_, i) => {
      const idx = i + 1;
      const cls = idx === state.step ? 'pip active' : (idx < state.step ? 'pip done' : 'pip');
      return `<span class="${cls}"></span>`;
    }).join('');
  };
  const paintProgress = () => {
    $('#step-num').textContent = state.step;
    $('#step-label').textContent = TOUR_LABELS[state.step - 1];
    const pct = ((state.step - 1) / TOUR_LABELS.length) * 100;
    $('#progress-fill').style.width = Math.max(8, pct) + '%';
    renderPips();
  };

  // ---------- tour scene routing ----------
  const showScene = (n) => {
    state.step = n;
    paintProgress();
    $$('[data-scene]').forEach(s => s.classList.toggle('hidden', +s.dataset.scene !== n));
    showView('tour');
  };

  // ---------- loginless boot ----------
  //
  // The SPA has no signup wall. At boot we mint (or recover) a stable
  // per-browser anonymous identity and hand it to the Hype-Lab backend so
  // the browser cookie carries the user's persona, credits, and any X
  // OAuth state across reloads. Scene 1 of the historical tour is skipped
  // entirely — every visitor starts inside the app.

  const bootAnonSession = async () => {
    const me = await api.ensureAnonSession(state.anonEmail);
    if (!me?.user) {
      toast('Backend unreachable — try again in a moment.');
      return false;
    }
    state.me = { ...me.user };
    state.persona = me.persona || null;

    // Auto-create a starter persona on first boot so the user can edit it
    // immediately from the top-menu without going through a signup gate.
    if (!state.persona) {
      try {
        const p = await api.savePersona({
          name: "Your voice",
          voice_description: '',
          target_audience: '',
          banned_words: '',
          style_examples: '',
        });
        if (p?.persona) state.persona = p.persona;
      } catch {}
    }

    $('#topmenu').classList.remove('hidden');
    paintTopMenu();
    return true;
  };

  // Re-pull `/api/lab/me` after operations that change the backend balance
  // (top-up, generate). Cheap; just keeps painters in sync.
  const refreshMe = async () => {
    try {
      const me = await api.me();
      if (me?.user) {
        state.me = { ...state.me, ...me.user };
        if (me.persona) state.persona = me.persona;
        paintTopMenu();
      }
    } catch {}
  };

  // ---------- scene 2: connect X ----------
  $('#connect-x-btn').addEventListener('click', async () => {
    const r = await api.oauthStart();
    if (r.authorize_url) location.href = r.authorize_url;
    else toast('OAuth error: ' + (r.error || 'unknown'));
  });
  $('#x-skip-btn').addEventListener('click', () => state.quickMode ? autoQuickFlow() : showScene(3));
  $('#x-continue').addEventListener('click', () => state.quickMode ? autoQuickFlow() : showScene(3));
  $('#x-advanced-toggle')?.addEventListener('click', () => {
    state.quickMode = false;
    toast('Advanced mode: pick list, kind, and hooks manually');
    showScene(3);
  });

  // Persona voice autosave (scene 2 + popover share the same call)
  let voiceTimer = null;
  $('#p-voice').addEventListener('input', () => {
    clearTimeout(voiceTimer);
    voiceTimer = setTimeout(async () => {
      if (!state.persona) return;
      const r = await api.persona({
        name: state.persona.name,
        voice_description: $('#p-voice').value,
        target_audience: state.persona.target_audience || '',
        banned_words: (state.persona.banned_words || []).join(', '),
        style_examples: (state.persona.style_examples || []).join('\n'),
      });
      if (r.persona) {
        state.persona = r.persona;
        const tag = $('#persona-saved');
        tag.classList.remove('hidden');
        setTimeout(() => tag.classList.add('hidden'), 1200);
      }
    }, 700);
  });

  // ---------- scene 3: pick a list ----------
  $('#load-lists-btn').addEventListener('click', async () => {
    if (!state.persona?.has_oauth) {
      toast('Connect X first (Step 2)');
      return;
    }
    $('#load-lists-btn').textContent = '⟳ loading…';
    const r = await api.lists();
    $('#load-lists-btn').textContent = '↻ Reload my X Lists';
    const area = $('#lists-area'); area.classList.remove('hidden');
    if (r.status !== 200) {
      area.innerHTML = `<div class="text-sm text-rose-300 font-mono">Could not load lists: ${esc(r.body?.error || r.status)}</div>`;
      return;
    }
    const lists = r.body.lists || [];
    if (!lists.length) {
      area.innerHTML = `<div class="text-sm text-white/55 italic px-2">You don't own any X Lists yet. See "How to make a List" above.</div>`;
      return;
    }
    area.innerHTML = lists.map(l => `
      <button data-list-id="${esc(l.id)}" data-list-name="${esc(l.name)}"
              class="w-full text-left p-4 rounded-2xl border border-white/10 bg-night-800 hover:border-sky-400 hover:bg-sky-400/5 transition flex items-center justify-between gap-3">
        <div class="min-w-0">
          <div class="font-semibold truncate">${esc(l.name)}</div>
          <div class="text-xs text-white/45 font-mono mt-0.5">id ${esc(l.id)} · ${l.member_count ?? '?'} members${l.private ? ' · private' : ''}</div>
        </div>
        <span class="text-sky-300 shrink-0">→</span>
      </button>`).join('');
    area.querySelectorAll('[data-list-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        state.sourceType = 'list';
        state.sourceId = btn.dataset.listId;
        persistLastSource(btn.dataset.listName);
        toast('List: ' + btn.dataset.listName);
        prefetchSource();  // warm X cache while user moves to step 4
        // In quick mode, skip scenes 4 and 5 — sensible defaults are in
        // state already. Power users (quickMode=false) get the full path.
        if (state.quickMode) {
          if (!state.selectedHooks.length) state.selectedHooks = ['contrarian','educational','shortbold'];
          enterPaywall();
        } else {
          showScene(4);
        }
      });
    });
  });

  // Manual source pills
  let manualSrcType = 'user';
  $$('.src-pill').forEach(p => p.addEventListener('click', () => {
    manualSrcType = p.dataset.src;
    $$('.src-pill').forEach(o => o.classList.toggle('bg-white/8', o === p));
  }));
  $('#src-manual-continue').addEventListener('click', () => {
    const v = $('#src-id-input').value.trim();
    if (!v) { toast('Paste something first'); return; }
    state.sourceType = manualSrcType;
    state.sourceId = v;
    persistLastSource(`${manualSrcType}: ${v}`);
    prefetchSource();
    if (state.quickMode) {
      if (!state.selectedHooks.length) state.selectedHooks = ['contrarian','educational','shortbold'];
      enterPaywall();
    } else {
      showScene(4);
    }
  });

  // ---------- scene 4: replies vs posts ----------
  const REPLY_SAMPLE = { hook: 'contrarian', text: 'decent thesis. who pays for it?', color: 'text-violet-300' };
  const POST_SAMPLE = { hook: 'shortbold', text: 'you don\'t need a roadmap. you need a list and a Friday', color: 'text-amber-300' };
  const paintKindSample = () => {
    const s = state.kind === 'posts' ? POST_SAMPLE : REPLY_SAMPLE;
    $('#kind-sample-label').textContent = state.kind === 'posts' ? 'Example post' : 'Example reply';
    $('#kind-sample').innerHTML = `
      <div class="text-[10px] uppercase tracking-widest ${s.color} mb-1.5">${s.hook} · ${state.kind === 'posts' ? 'post' : 'reply'}</div>
      ${esc(s.text)}`;
  };
  $$('input[name=kind]').forEach(r => r.addEventListener('change', () => {
    state.kind = r.value;
    state.kindCount = state.kind === 'posts' ? 3 : 20;
    $('#kind-count').value = state.kindCount;
    $('#kind-count-val').textContent = state.kindCount;
    paintKindSample();
  }));
  $('#kind-count').addEventListener('input', e => {
    state.kindCount = +e.target.value;
    $('#kind-count-val').textContent = state.kindCount;
  });
  $('#step4-continue').addEventListener('click', () => showScene(5));

  // ---------- scene 5: hooks ----------
  const HOOK_SAMPLES = {
    ragebait:    'this is the kind of decision that gets a CTO fired in 2026',
    funny:       'every time I read "leverage AI" I lose another nerve',
    polarizing:  'half of you will hate this and the other half retweet it',
    mock:        'this is a slide deck cosplaying as a product',
    shortbold:   'you\'re the product. the AI is the bill.',
    contrarian:  'decent thesis. who pays for it?',
    educational: 'the chart says one thing; the cap table says another',
  };
  const renderHooks = () => {
    $('#hook-chips').innerHTML = HOOK_STYLES.map(h => `
      <label class="cursor-pointer">
        <input type="checkbox" value="${esc(h)}" ${state.selectedHooks.includes(h) ? 'checked' : ''} class="peer sr-only">
        <span class="hook-chip inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-sm font-medium peer-checked:opacity-100 opacity-60 transition h-${esc(h)}" data-hook="${esc(h)}">
          ${esc(h)}
        </span>
      </label>`).join('');
    // Click handlers — also show example
    $$('#hook-chips .hook-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const h = chip.dataset.hook;
        // toggle
        const idx = state.selectedHooks.indexOf(h);
        if (idx === -1) state.selectedHooks.push(h);
        else state.selectedHooks.splice(idx, 1);
        // re-render chips to update peer-checked
        renderHooks();
        // example
        $('#hook-example').innerHTML = `
          <div class="text-[10px] uppercase tracking-widest mb-1.5 h-${esc(h)} inline-block px-2 py-0.5 rounded-full">${esc(h)}</div>
          <div class="mt-1">${esc(HOOK_SAMPLES[h] || '—')}</div>`;
      });
    });
  };
  $('#step5-continue').addEventListener('click', () => {
    if (state.selectedHooks.length === 0) { toast('Pick at least one hook style'); return; }
    enterPaywall();
  });

  // ---------- paywall ----------
  // Top-up tiers — the 4 preset chips. Bonus % shapes both the chip label
  // (rendered in i18n) and the credits/$ ratio for custom amounts.
  // computeCredits() uses the closest tier at or below the chosen USD.
  const TOPUP_TIERS = [
    { usd: 5,  credits: 5,  ratio: 1.00 },
    { usd: 10, credits: 12, ratio: 1.20 },
    { usd: 25, credits: 35, ratio: 1.40 },
    { usd: 50, credits: 75, ratio: 1.50 },
  ];
  const computeCredits = (usd) => {
    if (usd < 5) return 0;
    // Pick the highest tier whose USD is <= usd, apply its ratio.
    let tier = TOPUP_TIERS[0];
    for (const t of TOPUP_TIERS) if (usd >= t.usd) tier = t;
    return Math.round(usd * tier.ratio);
  };
  const formatAmount = (usd) => '$' + usd;
  const enterPaywall = () => {
    paintPaywall();
    paintAmountUi();
    paintReplyLangMirrors();
    paintPreviewCard();
    showView('paywall');
  };
  const paintPaywall = () => {
    const c = state.me?.credits ?? 0;
    $('#pw-credits').textContent = c;
    if (c >= 1) {
      $('#pw-topup').classList.add('hidden');
      $('#pw-generate').classList.remove('hidden');
      $('#pw-kind-count').textContent = state.kindCount;
      $('#pw-kind-label').textContent = state.kind;
    } else {
      $('#pw-topup').classList.remove('hidden');
      $('#pw-generate').classList.add('hidden');
    }
  };
  // Paint the chip-active state, slider value, readout, credits-preview,
  // hook text, and the topup button label. Cheap to call repeatedly.
  const paintAmountUi = () => {
    const amt = state.selectedTopupAmount;
    $$('.amount-chip').forEach(c => c.classList.toggle('active', +c.dataset.amount === amt));
    const slider = $('#pw-amount-slider'); if (slider) slider.value = amt;
    const val = $('#pw-amount-val'); if (val) val.textContent = amt;
    const cr = computeCredits(amt);
    const crPreview = $('#pw-credits-preview'); if (crPreview) crPreview.textContent = cr;
    const hook = $('#pw-amount-hook');
    if (hook) {
      // Loss-framed copy: tell the user what they're LEAVING ON THE TABLE
      // below $10, rather than just nudging them up. Pure gain-framing
      // below the anchor leaks ~20% of conversions to the $5 chip.
      if (amt < 10) {
        const lost = 12 - cr;   // credits they'd gain by bumping to $10
        hook.innerHTML = `<span class="text-rose-200">You'd skip ${lost} bonus credit${lost === 1 ? '' : 's'}.</span> $10 unlocks the +20% tier (12 cr instead of ${cr}).`;
      }
      else if (amt < 25) hook.textContent = `+20% bonus active. $25 unlocks +40% (+10 more free credits).`;
      else if (amt < 50) hook.textContent = `+40% bonus active. $50 unlocks +50% (+25 more free credits).`;
      else                hook.textContent = `+50% bonus active. Maximum value tier.`;
    }
    const lbl = $('#pw-topup-btn-label');
    if (lbl) {
      lbl.setAttribute('data-i18n-vars', JSON.stringify({ amount: formatAmount(amt) }));
      lbl.textContent = t('paywall.cta', { amount: formatAmount(amt) });
    }
  };
  // Update state + repaint immediately; persist to localStorage on a 200ms
  // trailing debounce so the slider doesn't thrash the disk during drag.
  let _topupPersistTimer = null;
  const setTopupAmount = (usd, { persist = true } = {}) => {
    const clamped = Math.max(5, Math.min(100, Math.round(usd)));
    state.selectedTopupAmount = clamped;
    paintAmountUi();
    if (!persist) return;
    clearTimeout(_topupPersistTimer);
    _topupPersistTimer = setTimeout(() => {
      try { localStorage.setItem('hybrid.topup', String(clamped)); } catch {}
    }, 200);
  };
  // Chip + slider listeners (registered once at boot — paint() drives state).
  // Chips persist immediately (discrete intent). Slider uses input for the
  // repaint and change for the final persist (debounced setItem in between
  // handles intermediate values cheaply).
  $$('.amount-chip').forEach(chip => chip.addEventListener('click', () => {
    setTopupAmount(+chip.dataset.amount);
  }));
  const slider = $('#pw-amount-slider');
  if (slider) {
    slider.addEventListener('input', e => setTopupAmount(+e.target.value));
    slider.addEventListener('change', e => {
      // Force the final value to flush immediately when the user releases.
      clearTimeout(_topupPersistTimer);
      try { localStorage.setItem('hybrid.topup', String(state.selectedTopupAmount)); } catch {}
    });
  }

  // Top-up — loginless. Calls the Hype-Lab backend directly to credit the
  // current anonymous session. The backend converts USD → credits using the
  // same tier table the paywall UI shows. No Stripe redirect, no third party
  // — the backend is the source of truth.
  $('#pw-topup-btn').addEventListener('click', async () => {
    if (!state.me) { toast('Session still loading — try again in a sec'); return; }
    const btn = $('#pw-topup-btn');
    const lbl = $('#pw-topup-btn-label');
    const prev = lbl ? lbl.textContent : btn.textContent;
    btn.disabled = true;
    if (lbl) lbl.textContent = '⟳ topping up…'; else btn.textContent = '⟳ topping up…';
    try {
      const credits = computeCredits(state.selectedTopupAmount);
      const r = await api.topup(credits);
      if (r.status !== 200) throw new Error(r.body?.error || ('HTTP ' + r.status));
      await refreshMe();
      paintPaywall();
      toast(t('checkout.added', { credits }));
    } catch (e) {
      toast('Could not top up: ' + (e.message || e));
    } finally {
      btn.disabled = false;
      if (lbl) lbl.textContent = prev; else btn.textContent = prev;
    }
  });
  $('#pw-generate-btn').addEventListener('click', runGenerate);
  $('#pw-back').addEventListener('click', () => showScene(state.quickMode ? 2 : 5));

  // Paint the reciprocity preview card from state.currentOutputs (a preview
  // run will have populated it before the user reached the paywall in some
  // flows). Safe no-op when nothing is available yet.
  const paintPreviewCard = () => {
    const card = $('#pw-preview'); if (!card) return;
    const previewOut = (state.currentOutputs || []).find(o => o._isPreview);
    if (!previewOut?.text) { card.classList.add('hidden'); return; }
    card.classList.remove('hidden');
    const tx = $('#pw-preview-text');
    if (tx) tx.textContent = previewOut.text;
  };

  // ---------- language popover ----------
  // Two modes: 'reply' edits state.replyLanguage (sent as options.language
  // to the generate endpoint); 'ui' edits state.locale (which UI strings
  // honour via [data-i18n]). Declared with `function` so the boot block —
  // which references it via `typeof openLangPopover === 'function'` — can
  // reach it regardless of source order.
  let _langPopoverMode = 'reply';
  function openLangPopover(mode) {
    _langPopoverMode = mode === 'ui' ? 'ui' : 'reply';
    const title = $('#lang-popover-title');
    if (title) title.textContent = _langPopoverMode === 'ui' ? t('lang.ui') : t('lang.reply');
    // Highlight the currently-selected language.
    const current = _langPopoverMode === 'ui' ? state.locale : state.replyLanguage;
    $$('#lang-popover .lang-opt').forEach(b => {
      b.classList.toggle('border-fuchsia-400', b.dataset.lang === current);
      b.classList.toggle('bg-fuchsia-400/10', b.dataset.lang === current);
    });
    const pop = $('#lang-popover');
    pop.classList.remove('hidden');
    pop.classList.add('flex');
  }
  const closeLangPopover = () => {
    const pop = $('#lang-popover');
    pop.classList.add('hidden');
    pop.classList.remove('flex');
  };
  const paintReplyLangMirrors = () => {
    const label = LANG_LABELS[state.replyLanguage] || LANG_LABELS.en;
    const main = $('#pw-lang-current'); if (main) main.textContent = label;
    $$('.pw-lang-current-mirror').forEach(el => { el.textContent = label; });
  };
  // Wire the lang change buttons in the paywall + tour. They all open the
  // popover in 'reply' mode.
  $$('.pw-lang-change').forEach(b => b.addEventListener('click', () => openLangPopover('reply')));
  $('#lang-popover-close')?.addEventListener('click', closeLangPopover);
  $('#lang-popover')?.addEventListener('click', (e) => {
    if (e.target.id === 'lang-popover') closeLangPopover();
  });
  // ESC closes either popover (lang or persona) — matches expected modal UX
  // and satisfies WCAG 2.1.2 keyboard escape requirements.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const lang = $('#lang-popover');
    if (lang && !lang.classList.contains('hidden')) { closeLangPopover(); return; }
    const persona = $('#persona-popover');
    if (persona && !persona.classList.contains('hidden')) {
      persona.classList.add('hidden');
      persona.classList.remove('flex');
    }
  });
  $$('#lang-popover .lang-opt').forEach(b => b.addEventListener('click', () => {
    const lang = b.dataset.lang;
    if (_langPopoverMode === 'ui') {
      setLocale(lang);
      // Re-paint paywall labels and the UI-lang chip in the account menu.
      try { paintUiLangLabel(); } catch {}
      paintAmountUi();
      paintReplyLangMirrors();
    } else {
      state.replyLanguage = lang;
      try { localStorage.setItem('hybrid.replyLang', lang); } catch {}
      paintReplyLangMirrors();
    }
    closeLangPopover();
  }));
  // Make paintUiLangLabel visible to the popover handler regardless of
  // source order.
  function paintUiLangLabel() {
    const el = $('#ui-lang-current'); if (!el) return;
    const lab = LANG_LABELS[state.locale] || LANG_LABELS.en;
    el.textContent = lab.split(' ')[0] + ' ' + (state.locale || 'en').toUpperCase();
  }

  // ---------- generate (parallel preview + main) ----------
  async function runGenerate() {
    if (!state.sourceType || !state.sourceId) {
      toast('Source missing — back to step 3');
      showScene(3);
      return;
    }

    const baseOptions = {
      target_replies:    state.kind === 'replies' ? state.kindCount : 0,
      target_standalone: state.kind === 'posts'   ? state.kindCount : 0,
      hook_styles:       state.selectedHooks,
      model:             DEFAULT_MODEL,
      language:          state.replyLanguage || 'en',  // backend ignores if unknown
    };
    if (baseOptions.target_replies === 0 && baseOptions.target_standalone === 0) {
      baseOptions.target_replies = 1;
    }

    $('#pw-generate-error').classList.add('hidden');
    $('#pw-generate-btn').disabled = true;
    $('#pw-generate-btn').textContent = '⟳ enqueueing…';

    // Credit accounting lives in the Hype-Lab backend. We fire the generate
    // request directly; the backend gates on its own balance and returns
    // 402 if the anon user is out of credits.
    const mainResp = await api.generate({
      source_type: state.sourceType,
      source_id:   state.sourceId,
      options:     baseOptions,
    });
    $('#pw-generate-btn').disabled = false;
    $('#pw-generate-btn').textContent = 'Generate now (-1 credit)';

    if (mainResp.status !== 200) {
      if (mainResp.status === 402) {
        await refreshMe();
        paintPaywall();
        toast('Out of credits — top up first');
        return;
      }
      $('#pw-generate-error').textContent = mainResp.body?.error || ('HTTP ' + mainResp.status);
      $('#pw-generate-error').classList.remove('hidden');
      return;
    }
    // Backend just debited — pull the new balance so the top menu paints.
    refreshMe();
    // Persist the job and (when credits hit 1) drop a non-blocking toast
    // nudging a top-up before the next pack — keeps the hook warm without
    // adding subscriptions or fake urgency.
    persistLastJob(mainResp.body.job_id);
    paintLowCreditToast();

    // Fire the free preview job in parallel (no await — non-blocking).
    api.generate({
      source_type: state.sourceType,
      source_id:   state.sourceId,
      preview:     true,
      options:     baseOptions,
    }).then(r => {
      if (r.status === 200) {
        state.previewJobId = r.body.job_id;
      } else {
        // Preview failed — not fatal; the main job will still produce.
        state.previewDone = true;
        updateCounter();
      }
    }).catch(() => { state.previewDone = true; updateCounter(); });

    // Switch to Results view immediately with skeletons so the user feels
    // they're already in the destination, then stream outputs in.
    state.mainJobId = mainResp.body.job_id;
    state.previewDone = false;
    state.mainDone = false;
    state.currentOutputs = [];
    state.currentSourceTweets = [];
    state.seenOutputIds = new Set();
    renderResults();
    showView('results');
    $('#results-meta').textContent = 'Drafting your pack — preview ideas show up first, then the full batch fills in.';
    startPolling();
  }

  const startPolling = () => {
    stopPolling();
    state.pollTimer = setInterval(pollBoth, 1500);
  };
  const stopPolling = () => { if (state.pollTimer) { clearInterval(state.pollTimer); state.pollTimer = null; } };

  // Poll both jobs each tick; merge any new outputs into the displayed list.
  const pollBoth = async () => {
    const ids = [state.previewJobId, state.mainJobId].filter(Boolean);
    if (!ids.length) return;
    const results = await Promise.all(ids.map(id => api.job(id).catch(() => null)));

    let touched = false;
    let mainData = null;
    results.forEach((data, idx) => {
      if (!data || !data.job) return;
      const isMain = data.job.id === state.mainJobId;
      if (isMain) mainData = data;
      // Merge source tweets (de-duped by tweet_id)
      (data.source_tweets || []).forEach(t => {
        if (!state.currentSourceTweets.some(x => x.tweet_id === t.tweet_id)) {
          state.currentSourceTweets.push(t);
          touched = true;
        }
      });
      // Merge new outputs (de-duped by DB id)
      (data.outputs || []).forEach(o => {
        if (state.seenOutputIds.has(o.id)) return;
        state.seenOutputIds.add(o.id);
        state.currentOutputs.push({ ...o, _isPreview: data.job.is_preview });
        touched = true;
      });
      if (data.job.status === 'done' && isMain && !state.mainDone) {
        state.mainDone = true;
        state.currentJob = data.job;
        touched = true;
      }
      if (data.job.status === 'done' && !isMain && !state.previewDone) {
        state.previewDone = true;
        touched = true;
      }
      if (data.job.status === 'error') {
        if (isMain) {
          stopPolling();
          state.mainDone = true;
          toast('Main pipeline failed: ' + (data.job.error_message || 'unknown'));
        } else {
          state.previewDone = true;
        }
        touched = true;
      }
    });

    if (touched) renderResults();

    // Stop polling once main is done. We allow up to ~3 minutes regardless.
    if (state.mainDone && state.previewDone) {
      stopPolling();
      if (mainData) {
        const cost = (+mainData.job.total_cost || 0).toFixed(4);
        $('#results-meta').textContent =
          `${mainData.job.model_used || ''} · pipeline cost $${cost} · charged 1 credit · ${state.currentOutputs.length} ideas in ${new Date(mainData.job.created_at).toLocaleString()}`;
      }
    } else if (state.mainDone) {
      // Main is done but preview isn't (rare). Stop polling after a beat.
      stopPolling();
    }
  };

  // ---------- results / outputs (X.com-feed look) ----------
  const buildTweetUrl = (username, tweetId) => {
    const safe = (username && /^[A-Za-z0-9_]+$/.test(username)) ? username : 'i/web';
    return `https://x.com/${safe}/status/${encodeURIComponent(tweetId)}`;
  };
  const COMPOSE_URL = 'https://x.com/compose/post';
  const composeIntentUrl = (text, inReplyTo) => {
    const p = new URLSearchParams({ text });
    if (inReplyTo) p.set('in_reply_to', inReplyTo);
    return 'https://x.com/intent/post?' + p.toString();
  };
  const READMORE_LIMIT = 280;

  // Deterministic gradient avatar from a handle string (when we don't have
  // the author's real profile image).
  const gradientFromHandle = (handle) => {
    const s = (handle || 'anon').toLowerCase();
    let hash = 0;
    for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
    const h1 = hash % 360;
    const h2 = (h1 + 120) % 360;
    return `conic-gradient(from 0deg, hsl(${h1} 70% 60%), hsl(${h2} 75% 55%), hsl(${h1} 70% 60%))`;
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    const d = Math.floor((Date.now() - t) / 1000);
    if (d < 60)   return d + 's';
    if (d < 3600) return Math.floor(d/60) + 'm';
    if (d < 86400) return Math.floor(d/3600) + 'h';
    if (d < 86400*7) return Math.floor(d/86400) + 'd';
    return new Date(iso).toLocaleDateString(undefined, {month:'short', day:'numeric'});
  };

  const fmtBigNum = (n) => {
    n = +n || 0;
    if (n >= 1e6) return (n/1e6).toFixed(n >= 1e7 ? 0 : 1) + 'M';
    if (n >= 1e3) return (n/1e3).toFixed(n >= 1e4 ? 0 : 1) + 'K';
    return String(n);
  };

  // Detect a few common media URL shapes so we can render image vs video.
  const renderMediaRow = (urls) => {
    if (!Array.isArray(urls) || !urls.length) return '';
    const cells = urls.slice(0, 4).map(u => {
      const isVid = /\.(mp4|m3u8|mov|webm)(\?|$)/i.test(u);
      if (isVid) {
        return `<div class="relative rounded-xl overflow-hidden border border-white/10 bg-night-800 aspect-video flex items-center justify-center">
                  <video src="${esc(u)}" class="w-full h-full object-cover" muted preload="metadata" playsinline></video>
                  <span class="absolute inset-0 grid place-items-center text-3xl drop-shadow-lg pointer-events-none">▶</span>
                </div>`;
      }
      return `<div class="rounded-xl overflow-hidden border border-white/10 bg-night-800 aspect-video">
                <img src="${esc(u)}" class="w-full h-full object-cover" loading="lazy" onerror="this.parentElement.style.display='none'">
              </div>`;
    });
    const cls = urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2';
    return `<div class="mt-3 grid ${cls} gap-2">${cells.join('')}</div>`;
  };

  // Truncate-with-read-more for long tweet text.
  const renderTweetText = (text, idAttr) => {
    const t = String(text || '');
    if (t.length <= READMORE_LIMIT) {
      return `<div class="whitespace-pre-wrap text-white/95 leading-relaxed">${esc(t)}</div>`;
    }
    const head = t.slice(0, READMORE_LIMIT).replace(/\s+\S*$/, '');
    return `
      <div class="readmore" data-rm-id="${idAttr}">
        <div class="readmore-short whitespace-pre-wrap text-white/95 leading-relaxed">${esc(head)}…</div>
        <div class="readmore-full whitespace-pre-wrap text-white/95 leading-relaxed hidden">${esc(t)}</div>
        <button type="button" class="readmore-toggle mt-1 text-sky-400 hover:text-sky-300 text-sm font-medium">Read more ↓</button>
      </div>`;
  };

  // Avatar element for an author (uses real X profile image if we have it,
  // otherwise a deterministic gradient).
  const avatarHtml = (handle, imgUrl, size = 11) => {
    const initial = (handle || '?').slice(0, 1).toUpperCase();
    if (imgUrl) {
      return `<span class="w-${size} h-${size} rounded-full overflow-hidden border border-white/10 shrink-0 block">
                <img src="${esc(imgUrl)}" alt="" class="w-full h-full object-cover">
              </span>`;
    }
    return `<span class="w-${size} h-${size} rounded-full border border-white/10 shrink-0 grid place-items-center text-white font-semibold"
                  style="background: ${gradientFromHandle(handle)}">${esc(initial)}</span>`;
  };

  // ---------- counter strip ----------
  const updateCounter = () => {
    const replies = state.currentOutputs.filter(o => o.output_type === 'reply').length;
    const posts   = state.currentOutputs.filter(o => o.output_type === 'post').length;
    $('#counter-replies').textContent = replies;
    $('#counter-posts').textContent = posts;
    const live = $('#counter-live');
    const liveText = $('#counter-live-text');
    if (state.previewDone && state.mainDone) {
      live.className = 'ml-auto flex items-center gap-2 text-xs text-emerald-300';
      liveText.textContent = '✓ pack complete';
    } else if (state.mainDone) {
      live.className = 'ml-auto flex items-center gap-2 text-xs text-emerald-300';
      liveText.textContent = '✓ main pack ready';
    } else if (state.previewDone) {
      live.className = 'ml-auto flex items-center gap-2 text-xs text-amber-300';
      liveText.innerHTML = '<span class="relative flex w-2 h-2"><span class="absolute inset-0 rounded-full bg-amber-400 animate-pulse2"></span></span> preview ready · main pack still brewing…';
    } else {
      live.className = 'ml-auto flex items-center gap-2 text-xs text-amber-300';
      liveText.innerHTML = '<span class="relative flex w-2 h-2"><span class="absolute inset-0 rounded-full bg-amber-400 animate-pulse2"></span></span> drafting first ideas with a fast model…';
    }
  };

  // ---------- render results (whole grid; cheap to call repeatedly) ----------
  const renderResults = (outputs = state.currentOutputs, sourceTweets = state.currentSourceTweets, mount = '#results-grid') => {
    const grid = typeof mount === 'string' ? $(mount) : mount;
    if (!outputs.length) {
      grid.innerHTML = renderSkeletons();
      return;
    }
    // Sort: posts first (more user-relevant for "self-broadcast"), then by
    // descending order added (newest at the top), keeping preview outputs
    // at the front so the user sees something fast.
    const sorted = outputs.slice().sort((a, b) => {
      if ((b._isPreview ? 1 : 0) - (a._isPreview ? 1 : 0)) return (b._isPreview ? 1 : 0) - (a._isPreview ? 1 : 0);
      return 0;
    });
    grid.innerHTML = sorted.map((o, i) => threadCardHtml(o, i, sourceTweets)).join('');
    wireThreadCards(grid, sorted, sourceTweets);
    updateCounter();
  };

  // Skeleton placeholder rows — same borderless X-feed look as real outputs.
  const renderSkeletons = () => {
    return Array.from({length: 3}, () => `
      <article class="border-b border-white/10 px-4 py-3 animate-pulse2">
        <div class="flex gap-3">
          <div class="w-11 h-11 rounded-full bg-white/8 shrink-0"></div>
          <div class="flex-1 space-y-2">
            <div class="flex gap-2">
              <div class="h-3 bg-white/12 rounded w-24"></div>
              <div class="h-3 bg-white/6 rounded w-16"></div>
              <div class="h-3 bg-white/6 rounded w-10"></div>
            </div>
            <div class="h-3 bg-white/8 rounded mt-3"></div>
            <div class="h-3 bg-white/8 rounded w-11/12"></div>
            <div class="h-3 bg-white/6 rounded w-2/3"></div>
            <div class="flex gap-6 mt-3">
              <div class="h-3 w-8 bg-white/6 rounded"></div>
              <div class="h-3 w-8 bg-white/6 rounded"></div>
              <div class="h-3 w-8 bg-white/6 rounded"></div>
            </div>
          </div>
        </div>
      </article>`).join('');
  };

  // ---------- thread card (X.com feed look — pure black, hairline dividers) ----------
  // SVG icons used in the action row, sized small like X's UI.

  // X-style action stat (icon + count), sized + spaced like the feed.
  const xStat = (icon, count, colorClass = 'hover:text-sky-400') => `
    <button class="group flex items-center gap-2 text-white/55 ${colorClass} transition-colors text-[13px]">
      <span class="p-2 -m-2 rounded-full group-hover:bg-current/10 transition-colors">${icon}</span>
      ${count !== null && count !== undefined ? `<span class="tabular-nums">${fmtBigNum(count)}</span>` : ''}
    </button>`;

  const threadCardHtml = (o, i, sourceTweets) => {
    const tg = o.target_tweet_id && sourceTweets.find(t => t.tweet_id === o.target_tweet_id);
    const isReply = o.output_type === 'reply';
    const youHandle = state.persona?.oauth_x_username || state.me?.name || 'you';
    const youAvatar = state.me?.x_profile_image_url || null;
    const yourName = state.me?.x_display_name || state.me?.name || 'You';

    // ---- PARENT TWEET (full X-feed look, replies only) ----
    let parentBlock = '';
    if (isReply && tg) {
      parentBlock = `
        <div class="parent-tweet px-4 pt-3 pb-2 hover:bg-white/[0.025] transition-colors cursor-pointer"
             data-open-url="${esc(buildTweetUrl(tg.author_username, tg.tweet_id))}">
          <div class="flex gap-3">
            <!-- Avatar column with connector line -->
            <div class="flex flex-col items-center shrink-0">
              ${avatarHtml(tg.author_username, null, 11)}
              <div class="parent-connector w-[2px] flex-1 mt-1 bg-white/12"></div>
            </div>
            <div class="flex-1 min-w-0">
              <!-- Single-row header: name + verified + @handle + · + time + ø + … -->
              <div class="flex items-center gap-1 text-[15px] leading-tight">
                <span class="font-bold text-white truncate hover:underline">${esc(tg.author_username ? tg.author_username : 'unknown')}</span>
                <span class="text-sky-400 shrink-0">${ICON.verified}</span>
                <span class="text-white/50 truncate">@${esc(tg.author_username || '?')}</span>
                <span class="text-white/50">·</span>
                <span class="text-white/50">${esc(timeAgo(tg.created_at_x))}</span>
                <span class="ml-auto flex items-center gap-2 text-white/45">
                  <span class="hover:text-sky-400 cursor-default" title="Source tweet">${ICON.mute}</span>
                  <span class="hover:text-sky-400 cursor-default">${ICON.more}</span>
                </span>
              </div>
              <!-- Body -->
              <div class="mt-1 text-[15px]">${renderTweetText(tg.text, `t-${o.id}`)}</div>
              ${renderMediaRow(tg.media_urls)}
              <!-- Action row -->
              <div class="mt-3 mr-8 flex items-center justify-between max-w-[420px]">
                ${xStat(ICON.reply, tg.reply_count, 'hover:text-sky-400')}
                ${xStat(ICON.retweet, tg.retweet_count, 'hover:text-emerald-400')}
                ${xStat(ICON.heart, tg.like_count, 'hover:text-pink-400')}
                ${tg.view_count ? xStat(ICON.views, tg.view_count, 'hover:text-sky-400') : ''}
                <div class="flex items-center -mr-2">
                  ${xStat(ICON.bookmark, null, 'hover:text-sky-400')}
                  ${xStat(ICON.share, null, 'hover:text-sky-400')}
                </div>
              </div>
            </div>
          </div>
        </div>`;
    }

    // ---- YOUR DRAFTED TWEET (the AI output, X-feed style) ----
    // For the AI tweet, the bottom action row is replaced with our two
    // primary buttons (Open + Copy & open) — that's the funnel-closer.
    const previewBadge = o._isPreview
      ? `<span class="px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-300 text-[10px] uppercase tracking-widest border border-amber-400/30">preview</span>`
      : '';
    const hookChip = `<span class="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border h-${esc(o.hook_style)}">${esc(o.hook_style)}</span>`;

    const aiBlock = `
      <div class="ai-tweet px-4 ${isReply ? 'pb-3 pt-1' : 'pt-3 pb-3'}">
        <div class="flex gap-3">
          ${avatarHtml(youHandle, youAvatar, 11)}
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-1 text-[15px] leading-tight">
              <span class="font-bold text-white truncate">${esc(yourName)}</span>
              <span class="text-sky-400 shrink-0">${ICON.verified}</span>
              <span class="text-white/50 truncate">@${esc(youHandle)}</span>
              <span class="text-white/50">·</span>
              <span class="text-white/50">draft</span>
              <span class="ml-auto flex items-center gap-1.5">
                ${hookChip}
                ${previewBadge}
              </span>
            </div>
            ${isReply ? `<div class="mt-0.5 text-[13px] text-white/50">Replying to <span class="text-sky-400">@${esc(tg ? tg.author_username : '?')}</span></div>` : ''}
            <textarea class="card-ta mt-1 w-full text-[15px] leading-snug bg-transparent focus:outline-none resize-none text-white/95" rows="3"></textarea>
            <div class="mt-0.5 flex items-center justify-between text-[12px] text-white/45">
              <span class="font-mono"><span class="card-charcount">0</span>/${TWEET_MAX}</span>
              ${o.why_this_will_work ? `<span class="italic text-emerald-300/80 truncate ml-3 max-w-[60%]" title="${esc(o.why_this_will_work)}">★ ${esc(o.why_this_will_work)}</span>` : ''}
            </div>

            <!-- Action row: simple Open + Copy & Open (twitter-styled pills) -->
            <div class="mt-3 flex items-center gap-2 flex-wrap">
              <button class="card-deep inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-[14px] font-bold" title="Copy text to clipboard, then open on X — paste-and-post">
                <span class="text-[15px]">📋</span>
                Copy &amp; ${isReply ? 'open' : 'open compose'}
              </button>
              <a class="card-open inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-white/20 hover:bg-white/10 text-white/85 text-[14px] font-medium" target="_blank" rel="noopener" title="Open on X — no copy">
                ${isReply ? 'Open target' : 'Open compose'}
              </a>
              <button class="card-more inline-flex items-center gap-1 ml-auto px-3 py-1.5 rounded-full hover:bg-white/8 text-white/60 text-[12px]">
                <span class="card-more-chev">▾</span> more ideas
              </button>
            </div>
            <div class="card-status mt-2 text-[12px] hidden font-medium leading-snug"></div>
          </div>
        </div>
      </div>`;

    // ---- BOTTOM COLLAPSIBLE — multi-modal copy chips ----
    const hasModal = !!(o.gif_keyword || o.image_idea || o.video_idea || o.miniapp_idea);
    const modalBlock = hasModal ? `
      <div class="card-modal hidden px-4 pb-4 pt-2 bg-white/[0.02] border-t border-white/8">
        <div class="ml-[60px]">
          <div class="text-[11px] uppercase tracking-widest text-white/45 mb-2">More content ideas — tap any to copy</div>
          <div class="grid sm:grid-cols-2 gap-2">
            ${o.gif_keyword ? `
              <button class="copy-chip flex items-start gap-2 p-3 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-left" data-copy-text="${esc(o.gif_keyword)}">
                <span class="text-base shrink-0">🖼</span>
                <div class="min-w-0 flex-1">
                  <div class="text-[10px] uppercase tracking-widest text-fuchsia-300">GIF keyword</div>
                  <div class="mt-0.5 text-[13px] truncate">${esc(o.gif_keyword)}</div>
                  <a href="https://giphy.com/search/${encodeURIComponent(o.gif_keyword)}" target="_blank" rel="noopener" onclick="event.stopPropagation()"
                     class="text-[10px] text-sky-400 hover:underline mt-1 inline-block">find GIF on giphy ↗</a>
                </div>
                <span class="copy-status text-[10px] text-emerald-300 hidden shrink-0">✓ copied</span>
              </button>` : ''}
            ${o.image_idea ? `
              <button class="copy-chip flex items-start gap-2 p-3 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-left" data-copy-text="${esc(o.image_idea)}">
                <span class="text-base shrink-0">🎨</span>
                <div class="min-w-0 flex-1">
                  <div class="text-[10px] uppercase tracking-widest text-sky-300">Image idea</div>
                  <div class="mt-0.5 text-[13px] line-clamp-3">${esc(o.image_idea)}</div>
                </div>
                <span class="copy-status text-[10px] text-emerald-300 hidden shrink-0">✓ copied</span>
              </button>` : ''}
            ${o.video_idea ? `
              <button class="copy-chip flex items-start gap-2 p-3 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-left" data-copy-text="${esc(o.video_idea)}">
                <span class="text-base shrink-0">🎬</span>
                <div class="min-w-0 flex-1">
                  <div class="text-[10px] uppercase tracking-widest text-violet-300">Video idea</div>
                  <div class="mt-0.5 text-[13px] line-clamp-3">${esc(o.video_idea)}</div>
                </div>
                <span class="copy-status text-[10px] text-emerald-300 hidden shrink-0">✓ copied</span>
              </button>` : ''}
            ${o.miniapp_idea ? `
              <button class="copy-chip flex items-start gap-2 p-3 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-left" data-copy-text="${esc(o.miniapp_idea)}">
                <span class="text-base shrink-0">⚡</span>
                <div class="min-w-0 flex-1">
                  <div class="text-[10px] uppercase tracking-widest text-amber-300">Vibe-codable prompt</div>
                  <div class="mt-0.5 text-[12px] text-white/75 line-clamp-3">paste into Cursor / v0 / Lovable → ${esc(o.miniapp_idea)}</div>
                </div>
                <span class="copy-status text-[10px] text-emerald-300 hidden shrink-0">✓ copied</span>
              </button>` : ''}
          </div>
        </div>
      </div>` : '';

    // Whole thread block — borderless with hairline divider underneath.
    return `
      <article class="card animate-fadein border-b border-white/10" data-idx="${i}" data-output-id="${o.id}">
        ${parentBlock}
        ${aiBlock}
        ${modalBlock}
      </article>`;
  };

  const wireThreadCards = (grid, outputs, sourceTweets) => {
    grid.querySelectorAll('.card').forEach((card, i) => {
      const o = outputs[i];
      const ta = card.querySelector('.card-ta');
      const charEl = card.querySelector('.card-charcount');
      ta.value = o.text;
      const updChars = () => { charEl.textContent = ta.value.length; };
      updChars(); ta.addEventListener('input', updChars);

      const tg = o.target_tweet_id && sourceTweets.find(t => t.tweet_id === o.target_tweet_id);
      const isReply = o.output_type === 'reply';

      // Simple open button — for replies opens the target tweet, for posts
      // opens X compose with the prefilled text via intent URL.
      const openBtn = card.querySelector('.card-open');
      const updateOpenHref = () => {
        if (isReply) {
          openBtn.href = buildTweetUrl(tg && tg.author_username, o.target_tweet_id);
        } else {
          openBtn.href = composeIntentUrl(ta.value, null);
        }
      };
      updateOpenHref();
      if (!isReply) ta.addEventListener('input', updateOpenHref);

      // Copy & open — the two-step pattern with inline status text.
      const deepBtn = card.querySelector('.card-deep');
      const status = card.querySelector('.card-status');
      const showStatus = (msg, tone) => {
        status.textContent = msg;
        status.classList.remove('hidden','text-emerald-300','text-rose-300','text-white/60');
        status.classList.add(tone === 'ok' ? 'text-emerald-300' : tone === 'err' ? 'text-rose-300' : 'text-white/60');
      };
      deepBtn.addEventListener('click', async () => {
        const text = ta.value;
        const openUrl = isReply
          ? buildTweetUrl(tg && tg.author_username, o.target_tweet_id)
          : COMPOSE_URL;
        const openMsg = IS_MOBILE
          ? (isReply ? 'opening the X app at the target post…' : 'opening the X app compose…')
          : (isReply ? 'opening the target tweet in a new tab…' : 'opening X compose in a new tab…');
        deepBtn.disabled = true;
        showStatus('Step 1/2 — copying recommended ' + (isReply ? 'reply' : 'post') + '…', 'neutral');
        let ok = false;
        try { await navigator.clipboard.writeText(text); ok = true; } catch (_) {}
        showStatus(ok
          ? `✓ Step 1: copied · Step 2: ${openMsg}`
          : `✗ Step 1 blocked — copy manually · Step 2: ${openMsg}`, ok ? 'ok' : 'err');
        setTimeout(() => {
          window.open(openUrl, '_blank', 'noopener');
          setTimeout(() => {
            showStatus(ok
              ? (isReply ? '✓ Done — paste the reply in the X reply box.' : '✓ Done — paste and post in X.')
              : 'X opened — copy the text manually first.', ok ? 'ok' : 'err');
            setTimeout(() => status.classList.add('hidden'), 5000);
            deepBtn.disabled = false;
          }, 350);
        }, 450);
      });

      // Bottom collapsible "more ideas"
      const moreBtn = card.querySelector('.card-more');
      const modal = card.querySelector('.card-modal');
      const chev = card.querySelector('.card-more-chev');
      if (moreBtn && modal) {
        moreBtn.addEventListener('click', () => {
          modal.classList.toggle('hidden');
          chev.textContent = modal.classList.contains('hidden') ? '▸' : '▾';
        });
      } else if (moreBtn) {
        moreBtn.disabled = true;
        moreBtn.classList.add('opacity-30');
      }

      // Multi-modal copy chips
      card.querySelectorAll('.copy-chip').forEach(chip => {
        chip.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(chip.dataset.copyText);
            const s = chip.querySelector('.copy-status');
            s.classList.remove('hidden');
            setTimeout(() => s.classList.add('hidden'), 1800);
          } catch (_) { toast('Copy blocked'); }
        });
      });

      // Read more toggle for the parent tweet text
      card.querySelectorAll('.readmore').forEach(rm => {
        const shortEl = rm.querySelector('.readmore-short');
        const fullEl  = rm.querySelector('.readmore-full');
        const tog     = rm.querySelector('.readmore-toggle');
        tog.addEventListener('click', (e) => {
          e.stopPropagation();
          const showingFull = !fullEl.classList.contains('hidden');
          shortEl.classList.toggle('hidden', !showingFull);
          fullEl.classList.toggle('hidden', showingFull);
          tog.textContent = showingFull ? 'Read more ↓' : 'Show less ↑';
        });
      });

      // Click the parent-tweet body (outside any inner button) opens the
      // original tweet on X, exactly like X's own feed does.
      const parent = card.querySelector('.parent-tweet');
      if (parent && parent.dataset.openUrl) {
        parent.addEventListener('click', (e) => {
          // Don't fire when clicking an actual button / link / textarea inside.
          if (e.target.closest('button, a, textarea, .readmore-toggle, video')) return;
          window.open(parent.dataset.openUrl, '_blank', 'noopener');
        });
      }
    });
  };

  // ---------- results / history nav ----------
  $('#results-new').addEventListener('click', () => {
    // back to scene 3 so user can pick another list — persona/X are kept
    state.sourceType = null; state.sourceId = null;
    showScene(3);
  });
  $('#history-new').addEventListener('click', () => {
    state.sourceType = null; state.sourceId = null;
    showScene(3);
  });

  // ---------- history view ----------
  const renderHistory = async () => {
    showView('history');
    $('#history-list').innerHTML = '<p class="text-sm text-white/40 italic">Loading…</p>';
    const r = await api.jobsList();
    const jobs = (r.jobs || []).filter(j => j.status === 'done');
    state.recentJobs = jobs;
    if (!jobs.length) {
      $('#history-list').innerHTML = '<p class="text-sm text-white/50 italic">No packs yet — create your first one.</p>';
      return;
    }
    // Group by yyyy-mm-dd
    const groups = {};
    jobs.forEach(j => {
      const day = (j.created_at || '').slice(0, 10);
      (groups[day] = groups[day] || []).push(j);
    });
    const todayKey = new Date().toISOString().slice(0, 10);
    const yKey = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const dayLabel = (k) => k === todayKey ? 'Today' : k === yKey ? 'Yesterday' : new Date(k + 'T00:00:00').toLocaleDateString(undefined, {weekday:'short', month:'short', day:'numeric'});
    $('#history-list').innerHTML = Object.keys(groups).sort().reverse().map(day => `
      <section>
        <h3 class="text-xs uppercase tracking-[0.25em] text-white/45 mb-2">${dayLabel(day)}</h3>
        <div class="space-y-2">
          ${groups[day].map(j => `
            <button data-job-id="${j.id}"
                    class="w-full text-left p-4 rounded-2xl bg-night-900 border border-white/8 hover:border-violet-400 hover:bg-violet-400/5 transition flex items-center gap-3">
              <div class="min-w-0 flex-1">
                <div class="font-semibold truncate">${esc(j.source_label || j.source_id)}</div>
                <div class="text-xs text-white/45 mt-0.5">${esc(j.source_type)} · ${j.source_tweet_count} cached · ${esc(j.model_used || '')}</div>
              </div>
              <div class="text-right shrink-0">
                <div class="text-sm font-mono text-white/70">${new Date(j.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                <div class="text-xs text-amber-300/80">1 credit</div>
              </div>
            </button>
            <div data-job-detail="${j.id}" class="hidden space-y-3 mt-2 mb-4 pl-3 border-l-2 border-white/8"></div>
          `).join('')}
        </div>
      </section>
    `).join('');
    $$('#history-list [data-job-id]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = +btn.dataset.jobId;
        const detail = document.querySelector(`[data-job-detail="${id}"]`);
        if (!detail.classList.contains('hidden')) {
          detail.classList.add('hidden');
          detail.innerHTML = '';
          return;
        }
        detail.classList.remove('hidden');
        detail.innerHTML = '<p class="text-xs text-white/40 italic">Loading outputs…</p>';
        const data = await api.job(id);
        if (data.error) { detail.innerHTML = `<p class="text-rose-300 text-sm">${esc(data.error)}</p>`; return; }
        detail.innerHTML = '';
        const grid = document.createElement('div');
        grid.className = 'space-y-4';
        detail.appendChild(grid);
        grid.innerHTML = data.outputs.map((o, i) => threadCardHtml(o, i, data.source_tweets || [])).join('');
        wireThreadCards(grid, data.outputs, data.source_tweets || []);
      });
    });
  };
  $('#history-btn').addEventListener('click', renderHistory);

  // ---------- persona popover ----------
  const openPersonaPopover = () => {
    if (!state.persona) return;
    $('#pp-name').value = state.persona.name || '';
    $('#pp-voice').value = state.persona.voice_description || '';
    $('#pp-audience').value = state.persona.target_audience || '';
    $('#pp-banned').value = (state.persona.banned_words || []).join(', ');
    $('#pp-examples').value = (state.persona.style_examples || []).join('\n');
    $('#persona-popover').classList.remove('hidden');
    $('#persona-popover').classList.add('flex');
  };
  const closePersonaPopover = () => {
    $('#persona-popover').classList.add('hidden');
    $('#persona-popover').classList.remove('flex');
  };
  $('#persona-btn').addEventListener('click', openPersonaPopover);
  $('#persona-close').addEventListener('click', closePersonaPopover);
  $('#pp-save').addEventListener('click', async () => {
    const r = await api.persona({
      name: $('#pp-name').value,
      voice_description: $('#pp-voice').value,
      target_audience: $('#pp-audience').value,
      banned_words: $('#pp-banned').value,
      style_examples: $('#pp-examples').value,
    });
    if (r.persona) {
      state.persona = r.persona;
      paintTopMenu();
      $('#pp-saved').classList.remove('hidden');
      setTimeout(() => { $('#pp-saved').classList.add('hidden'); closePersonaPopover(); }, 900);
    } else if (r.error) {
      toast('Save failed: ' + r.error);
    }
  });

  // ---------- top-up button (top menu) — opens the paywall so users
  // see the chips + bonus tiers + risk-reversal copy instead of buying
  // a hardcoded amount with no choice.
  $('#topup-btn').addEventListener('click', () => {
    // Force the topup panel even if they currently have credits — the user
    // explicitly clicked to add more.
    const credits = state.me?.credits ?? 0;
    if (credits >= 1) {
      // Temporarily flip the panel so the chips are visible.
      enterPaywall();
      $('#pw-topup').classList.remove('hidden');
      $('#pw-generate').classList.add('hidden');
    } else {
      enterPaywall();
    }
  });

  // ---------- reset session ----------
  // Loginless mode has no real "logout" — this clears the anonymous
  // identity so the user gets a fresh persona / credits / OAuth state on
  // reload. Useful if they want to test the funnel or start over.
  $('#logout-btn')?.addEventListener('click', async () => {
    if (!confirm('Reset this browser session? Your persona, X connection, and credits will be cleared.')) return;
    try { await api.logout(); } catch {}
    try { localStorage.removeItem('hybrid.anonId'); } catch {}
    location.reload();
  });

  // Auto-flow used when the user has OAuth but no source picked. Quick mode
  // tries to pick the first available List and jump straight to paywall —
  // collapses 3 manual decisions (list pick, kind, hooks) into zero clicks.
  // If lists aren't available (no permission, none owned, etc.), falls back
  // to scene 3 so the user can paste a handle manually.
  const autoQuickFlow = async () => {
    showScene(3);                                      // show a real scene so the user sees progress
    $('#load-lists-btn').textContent = '⟳ finding viral source…';
    let r;
    try { r = await api.lists(); } catch { r = null; }
    if (!r || r.status !== 200 || !r.body?.lists?.length) {
      // Couldn't auto-pick — stay on scene 3 so the user can paste a source.
      $('#load-lists-btn').textContent = '↻ Show my X Lists';
      $('#load-lists-btn').click();                    // populate the list area anyway
      return;
    }
    const list = r.body.lists[0];
    state.sourceType = 'list';
    state.sourceId   = list.id;
    persistLastSource(list.name);
    if (!state.selectedHooks.length) state.selectedHooks = ['contrarian','educational','shortbold'];
    prefetchSource();
    toast('Auto-picked: ' + list.name);
    enterPaywall();
  };

  // Smart-routes the "Get started / Continue" CTA based on session state.
  // Always called from the landing's data-cta="start" buttons.
  //
  // Loginless: state.me / state.persona are guaranteed populated by
  // bootAnonSession() before the user can click anything. The first real
  // gate is X OAuth (scene 2).
  const enterFlow = () => {
    if (!state.me || !state.persona)        return showScene(2); // session still booting — land on connect-X anyway
    if (!state.persona.has_oauth)           return showScene(2); // connect X
    if (!state.sourceId && state.quickMode) return autoQuickFlow();
    if (!state.sourceId)                    return showScene(3); // pick a list (power mode)
    return enterPaywall();                                       // ready to generate
  };

  // Signed-in returning users see a slim "Welcome back" banner at the top
  // of the landing instead of the regular page top. The banner names the
  // exact next step they're missing (connect X, pick a list, etc) and its
  // "Continue →" button routes through the same enterFlow() smart-router.
  const paintWelcomeBack = () => {
    const banner = $('#welcome-back');
    if (!banner) return;
    if (!state.me) { banner.classList.add('hidden'); return; }
    banner.classList.remove('hidden');
    const name = state.me.x_display_name || state.me.name || 'friend';
    $('#welcome-name').textContent = name;
    const stage =
      !state.persona              ? '5-step setup' :
      !state.persona.has_oauth    ? 'X connection (step 2)' :
      !state.sourceId             ? 'list pick (step 3)' :
                                    'next pack — ready when you are';
    $('#welcome-stage').textContent = stage;
  };

  // ---------- CTA / boot ----------
  $$('[data-cta="start"]').forEach(b => b.addEventListener('click', enterFlow));

  // "Reply again to <last list>" — returning users only. Restores state
  // from localStorage, then routes through enterFlow() so users with no
  // credits land on the paywall, and users with credits skip straight to
  // generate.
  $$('[data-cta="rerun"]').forEach(b => b.addEventListener('click', () => {
    if (!state.lastSource?.id) return enterFlow();
    state.sourceType = state.lastSource.type;
    state.sourceId   = state.lastSource.id;
    enterFlow();
  }));

  // Follow-up banner — "see replies" opens the history view at the cached
  // job; "dismiss" clears so it doesn't keep nagging.
  const followupOpen    = $('#followup-open');
  const followupDismiss = $('#followup-dismiss');
  if (followupOpen) followupOpen.addEventListener('click', () => {
    $('#followup-banner')?.classList.add('hidden');
    // Route to history; if loadHistory is defined later it gets a click.
    $('#history-btn')?.click();
  });
  if (followupDismiss) followupDismiss.addEventListener('click', () => {
    state.lastJobAt = 0;
    try { localStorage.removeItem('hybrid.lastJobAt'); } catch {}
    $('#followup-banner')?.classList.add('hidden');
  });

  // UI language switcher — opens the lang-popover in 'ui' mode. The
  // `openLangPopover` function is declared further down with `function` so
  // hoisting reaches this binding.
  const uiLangBtn = $('#ui-lang-btn');
  if (uiLangBtn) uiLangBtn.addEventListener('click', () => openLangPopover('ui'));

  (async () => {
    // 1. Pull the config blob the Jinja-rendered SPA gets inline. Failing
    // here is fatal — the model catalog + hook list drive every scene.
    try {
      const cfg = await api.config();
      MODELS          = cfg.model_catalog || [];
      DEFAULT_MODEL   = cfg.default_model || '';
      HOOK_STYLES     = cfg.hook_styles   || [];
      TWEET_MAX       = cfg.tweet_max_chars || 280;
      CREDITS_PER_USD = cfg.credits_per_usd || 100;
      state.boot      = cfg;
    } catch (e) {
      console.error('Boot config fetch failed:', e);
      toast('Backend unreachable — check VITE_API_BASE.');
    }
    if (!state.selectedHooks.length) state.selectedHooks = HOOK_STYLES.slice();

    // 2. First paint of landing-only widgets.
    renderReel();
    renderHooks();
    paintKindSample();

    // 3. Loginless bootstrap. Establishes the anonymous backend cookie and
    //    populates state.me / state.persona so painters can run. Never
    //    blocks the UI — if the backend is unreachable, the landing still
    //    paints and the user can retry by clicking "Get started".
    await bootAnonSession();
    paintWelcomeBack();

    // 4. Apply i18n to anything with [data-i18n] before first paint.
    applyI18n();

    // 5. Route into the correct landing variant (via hash), or — for
    // returning users with a cached source — skip the tour and route
    // straight to the paywall. `?tour=1` forces the full tour back.
    const params = new URLSearchParams(location.search);
    const forceTour = params.get('tour') === '1';
    if (!forceTour && state.me && state.persona?.has_oauth && state.lastSource?.id) {
      state.sourceType = state.lastSource.type;
      state.sourceId   = state.lastSource.id;
      handleRoute();          // paint the landing the user came in on
    } else {
      handleRoute();
    }

    // 6. 24-h follow-up banner — if the user's last pack is 18–36h old,
    // surface a banner in the top menu so they re-enter and burn credits.
    paintFollowupBanner();
  })();
